import type { AffiliationType } from '../types/registration'

export const WORDMARK = 'Stepping Stone'

export const AFFILIATION_OPTIONS: { label: string; value: AffiliationType }[] = [
  { label: 'I was in a program here', value: 'alumni' },
  { label: 'I worked here', value: 'staff' },
  { label: 'I volunteered here', value: 'volunteer' },
  { label: 'I served on the board', value: 'board_member' },
  { label: 'I supported the work', value: 'supporter' },
  { label: 'I’m family or a friend of someone who was', value: 'family' },
]

export const PROGRAM_TYPES = [
  'Residential',
  'Stepping Out (outpatient)',
  'Sober living',
  'Aftercare and alumni',
  'Mental health services',
  'Day program',
  'Volunteer or event crew',
]

export const ATTRIBUTION_OPTIONS = [
  { label: 'First name and last initial', value: 'initials' },
  { label: 'My full first and last name', value: 'name' },
  { label: 'No name at all', value: 'anonymous' },
]

const FIRST_YEAR = 1976
const CURRENT_YEAR = 2026

export const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - FIRST_YEAR + 1 },
  (_, index) => {
    const year = String(CURRENT_YEAR - index)
    return { label: year, value: year }
  },
)

/** Aggregate community figures. Never per-person, never rankable. */
export const COMMUNITY = {
  members: 129,
  decadesRepresented: 6,
  collectiveYears: '1,850',
}

/** Recently arrived members, shown as first name + last initial only. */
export const RECENT_ARRIVALS: Record<string, string[]> = {
  '1970s': ['Marion A.', 'Ken D.'],
  '1980s': ['Dev R.', 'Paulette S.', 'Ray M.'],
  '1990s': ['Theresa M.', 'Andre P.', 'Lorna B.'],
  '2000s': ['Danielle W.', 'Owen T.', 'Priya N.'],
  '2010s': ['Nadia K.', 'Marcus J.', 'Elena V.'],
  '2020s': ['Sam O.', 'Iris L.'],
}

/** How many members are already here from each decade. */
export const DECADE_MEMBERS: Record<string, number> = {
  '1970s': 4,
  '1980s': 11,
  '1990s': 23,
  '2000s': 31,
  '2010s': 42,
  '2020s': 18,
}

export const PANEL_PHOTOS = {
  building: {
    src: 'https://cdn.magicpatterns.com/uploads/6HH9Dv9iaq6Ku5deW8zdN8/Stepping_Stone_exterior_2.png',
    alt: 'The Stepping Stone building: a stone tower beside a yellow stucco wing, a flag out front, palm shadows on the wall',
  },
  room: {
    src: 'https://cdn.magicpatterns.com/uploads/r47ZmpxHUyJATaRHGvaYDQ/courtyard.jpg',
    alt: 'The inner courtyard seen through olive trees, with a white umbrella over a red picnic table and balconies above',
  },
  hands: {
    src: 'https://cdn.magicpatterns.com/uploads/hg89bbDJBdcpzhV8zy2gbp/tile_wall.jpg',
    alt: 'The tile wall in the courtyard, engraved squares and hearts set into the stucco beside a wooden bench',
  },
  founding: {
    src: 'https://cdn.magicpatterns.com/uploads/3zi1jD3QiLqBBU7GPwRQ9M/friends.jpg',
    alt: 'A faded color print of about eighteen friends posing at night in front of a white wall and clipped hedges',
  },
  era: {
    src: 'https://cdn.magicpatterns.com/uploads/i5DQhCScfupCdyamWRYW3k/Camping_Trip_2.jpg',
    alt: 'A large alumni group on a camping trip, sitting and standing around a picnic bench in the pines',
  },
  voices: {
    src: 'https://cdn.magicpatterns.com/uploads/iBdg7EnfoMHXu923dR99zD/group2.jpg',
    alt: 'Longtime members gathered for a reunion photograph in front of an arched window',
  },
  sign: {
    src: 'https://cdn.magicpatterns.com/uploads/8dkiobBnDMA8vfFo2UyJmd/front_sign.jpg',
    alt: 'STEPPING STONE carved into the stone wall by the front steps, an agave and ornamental grasses below',
  },
  steps: {
    src: 'https://cdn.magicpatterns.com/uploads/bKKuWTTxCx2giPuHwfEcac/drag.jpg',
    alt: 'A crowd on the front steps under an “Everyone Belongs” banner, two performers in front',
  },
  holiday: {
    src: 'https://cdn.magicpatterns.com/uploads/cCEsrstVxZYhTcJXReboFt/grand_central.jpg',
    alt: 'A group gathered in the common room at Christmas, a wreath over the stone fireplace',
  },
  luncheon: {
    src: 'https://cdn.magicpatterns.com/uploads/cvF7TaeCqFMBFRsYyHc8q8/group.jpg',
    alt: 'Four friends smiling together at an outdoor luncheon under a pergola with balloons',
  },
}

interface EraNote {
  tag: string
  headline: string
  body: string
}

export const ERA_STORIES: Record<string, EraNote> = {
  '1970s': {
    tag: 'The 1970s',
    headline: 'A safe place to tell the truth.',
    body: 'In February 1976, charter members met at MCC on Fern Street in Golden Hill. By 1979, five small houses on Central Avenue had become Stepping Stone Central.',
  },
  '1980s': {
    tag: 'The 1980s',
    headline: 'Holding community.',
    body: 'Through the AIDS crisis, the houses on Central Avenue kept their doors open and held people through loss the wider world looked away from.',
  },
  '1990s': {
    tag: 'The 1990s',
    headline: 'A building that said: you matter.',
    body: 'A day program, the first gala, Our House in Golden Hill. In 1994 the Central Avenue property became ours, and by 1999 ground was broken on The Stone.',
  },
  '2000s': {
    tag: 'The 2000s',
    headline: 'Whole person, whole care.',
    body: 'The Stone opened in 2000. Enya House came to Hillcrest, Living Out Loud began, and mental health care grew alongside recovery.',
  },
  '2010s': {
    tag: 'The 2010s',
    headline: 'Growing influence.',
    body: 'Nonprofit of the Year in 2010, expanded dual diagnosis care, and a tile wall in the courtyard that kept filling in.',
  },
  '2020s': {
    tag: 'The 2020s',
    headline: 'Recovery strong.',
    body: 'Groups moved to telehealth in 2020. By 2024 residential had grown to 31 beds, and the fiftieth brought everyone back to the courtyard.',
  },
}

export const ERA_QUOTES: Record<string, EraNote> = {
  '1970s': {
    tag: 'In their words',
    headline: '“Somebody left the light on for us.”',
    body: 'Marion A., here in 1979.',
  },
  '1980s': {
    tag: 'In their words',
    headline: '“The back house on Central saved my life twice.”',
    body: 'Dev R., here 1984–1989.',
  },
  '1990s': {
    tag: 'In their words',
    headline: '“I learned to ask for help in that building.”',
    body: 'Theresa M., here 1996.',
  },
  '2000s': {
    tag: 'In their words',
    headline: '“I came back because someone did it for me.”',
    body: 'Andre P., here since 2004.',
  },
  '2010s': {
    tag: 'In their words',
    headline: '“It was the one place nobody rushed me.”',
    body: 'Nadia K., here 2013–2016.',
  },
  '2020s': {
    tag: 'In their words',
    headline: '“I did my first Pride on the Stepping Stone float.”',
    body: 'Sam O., here since 2021.',
  },
}

export function decadeFor(year: string): string {
  const parsed = Number(year)
  if (!parsed || Number.isNaN(parsed)) return '1990s'
  const decade = Math.floor(parsed / 10) * 10
  if (decade <= 1970) return '1970s'
  if (decade >= 2020) return '2020s'
  return `${decade}s`
}

export function membersFromDecade(year: string): number {
  return DECADE_MEMBERS[decadeFor(year)] ?? 0
}
